import React, {useContext, useEffect, useState} from 'react';
import {Badge, Button, DatePicker, Descriptions, InputNumber, Tag} from 'antd';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import {internalApi} from '../../../../common/axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

const EVENT_TYPE_TRUCK = 32;

const PlayerTruck = () => {
    const {playerData, setPlayerData} = useContext(PlayerContext);
    const playerInfo = playerData?.playerInfo;

    const liveEventCaches = playerInfo?.liveEventCache?.LiveEventCaches ?? {};
    const truckEventKey = Object.keys(liveEventCaches).find(key => liveEventCaches[key]?.EventType === EVENT_TYPE_TRUCK);
    const truckData = truckEventKey !== undefined ? liveEventCaches[truckEventKey]?.EventInfo?.TruckData : undefined;

    const eventTruckPoint = playerInfo?.walletData?.EventTruckPoint ?? 0;
    const truckTrigger = playerInfo?.liveEventCache?.TruckTrigger;

    const [form, setForm] = useState(null);
    const [isModified, setIsModified] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!truckData) {
            setForm(null);
            setIsModified(false);
            return;
        }
        setForm({
            currentStep: truckData.currentStep,
            stageClearStates: truckData.stageClearStates || [],
            eventTruckPoint,
            lastEndStamp: truckTrigger?.LastEndStamp ?? 0,
            lastEndSeq: truckTrigger?.LastEndSeq ?? 0,
            energyUsedSinceCooltime: truckTrigger?.EnergyUsedSinceCooltime ?? 0,
            chargeSinceCooltime: truckTrigger?.ChargeSinceCooltime ?? 0,
        });
        setIsModified(false);
    }, [playerData.playerInfo]);

    const handleFormChange = (key, value) => {
        setForm(prev => ({...prev, [key]: value}));
        setIsModified(true);
    };

    const handleStageToggle = (idx) => {
        setForm(prev => {
            const next = [...prev.stageClearStates];
            next[idx] = !next[idx];
            return {...prev, stageClearStates: next};
        });
        setIsModified(true);
    };

    const handleSave = async () => {
        if (!form || truckEventKey === undefined) return;
        setSaving(true);
        try {
            const truckEventCache = playerInfo.liveEventCache.LiveEventCaches[truckEventKey];
            const nextPlayerInfo = {
                ...playerInfo,
                liveEventCache: {
                    ...playerInfo.liveEventCache,
                    LiveEventCaches: {
                        ...playerInfo.liveEventCache.LiveEventCaches,
                        [truckEventKey]: {
                            ...truckEventCache,
                            EventInfo: {
                                ...truckEventCache.EventInfo,
                                TruckData: {
                                    ...truckEventCache.EventInfo.TruckData,
                                    currentStep: form.currentStep,
                                    stageClearStates: form.stageClearStates,
                                },
                            },
                        },
                    },
                },
                walletData: {
                    ...playerInfo.walletData,
                    EventTruckPoint: form.eventTruckPoint,
                },
            };
            nextPlayerInfo.liveEventCache.TruckTrigger = {
                ...truckTrigger,
                LastEndStamp: form.lastEndStamp,
                LastEndSeq: form.lastEndSeq,
                EnergyUsedSinceCooltime: form.energyUsedSinceCooltime,
                ChargeSinceCooltime: form.chargeSinceCooltime,
            };

            await internalApi.post('/player/playInfo', {
                playInfo: nextPlayerInfo,
                userKey: playerData.userKey,
                syncId: playerData.syncId,
            });

            setPlayerData(prev => ({...prev, playerInfo: nextPlayerInfo}));
            toast.success('저장되었습니다.');
        } catch (e) {
            toast.error(e?.response?.data?.message || '저장 실패');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{maxWidth: 900, margin: '0 auto', padding: 24}}>
            <h2 style={{marginBottom: 20}}>트럭 이벤트</h2>

            {!truckData || !form ? (
                <div style={{padding: 24, textAlign: 'center', color: '#999'}}>
                    트럭 이벤트 진행 데이터가 없습니다.
                </div>
            ) : (
                <>
                    <div style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}>
                        <h3 style={{marginBottom: 16}}>진행 정보</h3>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="완료 여부">
                                {truckData.isComplete
                                    ? <Badge status="success" text="완료"/>
                                    : <Badge status="processing" text="진행중"/>}
                            </Descriptions.Item>
                            <Descriptions.Item label="세그먼트 그룹">{truckData.segmentGroup}</Descriptions.Item>
                            <Descriptions.Item label="현재 단계">
                                <InputNumber
                                    value={form.currentStep}
                                    onChange={(v) => handleFormChange('currentStep', v)}
                                    min={0}
                                    style={{width: 140}}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="단계별 클리어(보상 지급) 상태">
                                {form.stageClearStates.map((cleared, idx) => (
                                    <Tag.CheckableTag
                                        key={idx}
                                        checked={cleared}
                                        onChange={() => handleStageToggle(idx)}
                                        style={{
                                            marginBottom: 4,
                                            border: '1px solid #d9d9d9',
                                            color: cleared ? undefined : 'rgba(0, 0, 0, 0.65)',
                                        }}
                                    >
                                        {idx + 1}단계: {cleared ? '지급됨' : '미지급'}
                                    </Tag.CheckableTag>
                                ))}
                            </Descriptions.Item>
                        </Descriptions>
                    </div>

                    <div style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}>
                        <h3 style={{marginBottom: 16}}>이벤트 재화</h3>
                        <Descriptions bordered size="small" column={1}>
                            <Descriptions.Item label="트럭 이벤트 포인트">
                                <InputNumber
                                    value={form.eventTruckPoint}
                                    onChange={(v) => handleFormChange('eventTruckPoint', v)}
                                    min={0}
                                    style={{width: 200}}
                                />
                            </Descriptions.Item>
                        </Descriptions>
                    </div>

                    <div style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}>
                        <h3 style={{marginBottom: 16}}>트럭 발동 정보</h3>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="마지막 종료 시각">
                                <DatePicker
                                    showTime
                                    value={form.lastEndStamp ? dayjs.unix(form.lastEndStamp) : null}
                                    onChange={(d) => handleFormChange('lastEndStamp', d ? d.unix() : 0)}
                                    format="YYYY-MM-DD HH:mm:ss"
                                    style={{width: 200}}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="마지막 종료 시퀀스">
                                <InputNumber
                                    value={form.lastEndSeq}
                                    onChange={(v) => handleFormChange('lastEndSeq', v)}
                                    min={0}
                                    style={{width: 140}}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="쿨타임 이후 사용 에너지">
                                <InputNumber
                                    value={form.energyUsedSinceCooltime}
                                    onChange={(v) => handleFormChange('energyUsedSinceCooltime', v)}
                                    min={0}
                                    style={{width: 140}}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="쿨타임 이후 충전량">
                                <InputNumber
                                    value={form.chargeSinceCooltime}
                                    onChange={(v) => handleFormChange('chargeSinceCooltime', v)}
                                    min={0}
                                    style={{width: 140}}
                                />
                            </Descriptions.Item>
                        </Descriptions>
                    </div>

                    <div style={{display: 'flex', justifyContent: 'center'}}>
                        <Button type="primary" onClick={handleSave} loading={saving} disabled={!isModified}>
                            저장
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default PlayerTruck;
